import { setParameter } from 'actions/setParam';
import { SET_CONFIRM_AUTO_PARK, SET_DRIVING_MODE_STATUS } from 'actions/types';
import React, { useMemo, useRef, useState, useEffect, useReducer } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { AppRootState } from 'reducers';
import { RobotPrimaryCamera } from 'types';
import Tooltip from '../tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import './index.scss';
import { translationWithErrorHandling } from '../../translation/handleMissingTranslation';

import genericAlertIcon from 'images/alert.svg';
import drivingViewIcon from 'images/driving-view.svg';
import flashImage from 'images/flash.svg';
import zoomInIcon from 'images/zoom-in.svg';
import zoomOutIcon from 'images/zoom-out.svg';
import stopRecordingIcon from 'images/stop-icon.svg';
import startRecordingIcon from 'images/record-icon.svg';
import { DataChannels } from 'GoBeWebRTC/types';

const reduxConnector = connect(
	(state: AppRootState) => ({
		controlDataChannel: state.sessionState.controlDataChannel,
		localVoiceVolume: state.sessionState.localVoiceVolume,
		hideNavOptionsStatus: state.sessionState.hideNavOptionsStatus,
		microphoneLevel: state.hardwareState.settings.microphone.microphoneLevel,
		drivingMode: state.sessionState.drivingMode,
		navCameraHeight: state.sessionState.navCameraHeight,
		autoParkEnabled: state.sessionState.autoParkEnabled,
		confirmAutoPark: state.sessionState.confirmAutoPark,
		isAutoParking: state.sessionState.isAutoParking,
		dockControllerStatus: state.sessionState.dockControllerStatus,
	}),
	{ setParameter }
);

type PropsFromParent = {
	onClickHangUp: () => void;
	localStream: MediaStream | null;
	navCameraRotation: number;
	hasPrimaryVideoStartedPlaying: boolean;
	isNavCameraVertical: boolean;
	dataChannels?: { [label: string]: RTCDataChannel };
};

type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
type ComponentProps = PropsFromRedux & PropsFromParent;

const NextCameraActionIcons: Record<RobotPrimaryCamera, string> = {
	[RobotPrimaryCamera.WIDE_CAM]: zoomInIcon,
	[RobotPrimaryCamera.ZOOM_CAM]: zoomOutIcon,
};

const RobotFeatures: React.FC<ComponentProps> = ({
	hasPrimaryVideoStartedPlaying,
	controlDataChannel,
	hideNavOptionsStatus,
	drivingMode,
	setParameter,
	autoParkEnabled,
	confirmAutoPark,
	isAutoParking,
	dockControllerStatus,
	dataChannels,
}: ComponentProps) => {
	const intl = useIntl();
	const isSessionOpen =
		hasPrimaryVideoStartedPlaying && controlDataChannel && controlDataChannel.readyState === 'open';
	const canAutoPark =
		autoParkEnabled &&
		((dockControllerStatus.stage === 'DETECTING' && dockControllerStatus.state === 'SUCCESS') ||
			(isAutoParking && ['STARTING', 'DOCKING', 'STOPPING'].includes(dockControllerStatus.stage)));

	const handleToggleDrivingMode = () => {
		setParameter('drivingMode', SET_DRIVING_MODE_STATUS, !drivingMode);
	};
	const onAutoParkClick = () => {
		setParameter('confirmAutoPark', SET_CONFIRM_AUTO_PARK, true);
		document.dispatchEvent(new CustomEvent('firstStartAutoPark'));
	};

	const autoParkButtonPopupText = useMemo(() => {
		if (!autoParkEnabled)
			return translationWithErrorHandling(intl, 'robotFeatures.autoparkNotAvailable');

		if (!canAutoPark)
			return translationWithErrorHandling(intl, 'robotFeatures.driveToChargingDock');

		return translationWithErrorHandling(intl, 'robotFeatures.autopark');
	}, [autoParkEnabled, canAutoPark]);

	const meetingViewTooltipShowing = useRef(false);
	const setMeetingViewTooltipShowing = (value: boolean) =>
		(meetingViewTooltipShowing.current = value);
	const autoparkTooltipShowing = useRef(false);
	const setAutoparkTooltipShowing = (value: boolean) => (autoparkTooltipShowing.current = value);

	const [tooltipWantsToShow, setTooltipWantsToShow] = useState(false);
	useEffect(() => {
		setTooltipWantsToShow(meetingViewTooltipShowing.current || autoparkTooltipShowing.current);
	}, [meetingViewTooltipShowing.current, autoparkTooltipShowing.current]);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	const [isRecording, setIsRecording] = useState(false);
	const [sequenceNumber, setSequenceNumber] = useState(0);

	const handleToggleRecording = () => {
		const newRecordingState = !isRecording;
		setIsRecording(newRecordingState);

		const newSequenceNumber = sequenceNumber + 1;
		setSequenceNumber(newSequenceNumber);

		// Send recording command through the CONTROL data channel
		if (dataChannels && dataChannels[DataChannels.CONTROL_DATACHANNEL]) {
			let nowInMs = Date.now();
			const message = JSON.stringify({
				type: 'recording',
				data: {
					sequence_number: sequenceNumber,
					action: newRecordingState ? 'start' : 'stop',
					timestamp_in_ms: nowInMs,
				},
			});
			console.debug('Sending ' + message);
			dataChannels[DataChannels.CONTROL_DATACHANNEL].send(message);
		}
	};

	return (
		<div
			className={hideNavOptionsStatus ? 'robotFeaturesContainer' : 'displayNone'}
			style={{ zIndex: tooltipWantsToShow ? 37 : undefined }}
			onMouseMove={() => forceUpdate()}
		>
			<Tooltip left text={isRecording ? 'Stop Recording' : 'Start Recording'}>
				<div
					className={`${isRecording ? 'greenCircle' : 'blackCircle'}`}
					onClick={handleToggleRecording}
				>
					<div className="iconWrapper">
						<img alt="recording" src={isRecording ? stopRecordingIcon : startRecordingIcon} />
					</div>
				</div>
			</Tooltip>

			<Tooltip
				left
				text={
					isSessionOpen
						? drivingMode
							? translationWithErrorHandling(intl, 'robotFeatures.meetingView')
							: translationWithErrorHandling(intl, 'robotFeatures.drivingView')
						: undefined
				}
				wantsToShow={setMeetingViewTooltipShowing}
				showAllowed={tooltipWantsToShow}
			>
				<div className="blackCircle" onClick={handleToggleDrivingMode}>
					<img className="iconWrapper" src={drivingViewIcon} alt="" />
				</div>
			</Tooltip>
			<Tooltip
				left
				showOnChange={canAutoPark}
				text={autoParkButtonPopupText}
				wantsToShow={setAutoparkTooltipShowing}
				showAllowed={tooltipWantsToShow}
			>
				<div
					className={`${
						isAutoParking
							? 'greenCircle'
							: `${confirmAutoPark ? 'selectedBlackCircle' : 'blackCircle'}`
					} ${!canAutoPark && 'disabled'}`}
					onClick={onAutoParkClick}
				>
					<img className="iconWrapper" src={flashImage} alt="" />
					{dockControllerStatus.stage === 'DOCKING' && dockControllerStatus.state === 'FAILED' ? (
						<img className="badge" src={genericAlertIcon} alt="" />
					) : null}
				</div>
			</Tooltip>
		</div>
	);
};

export default reduxConnector(RobotFeatures);
